/**
 * Theme bridge: resolves the --chart-* CSS variables into Highcharts global
 * options and repaints every live chart. Surface/text/grid tokens must be
 * resolved to raw strings (Highcharts uses them for measurements and canvas
 * ops); series colours are passed as `var(--chart-*)` literals inside the
 * chart components so the SVG flips with `.dark` without a re-render.
 *
 * Called from ChartsSection on mount and whenever ThemeContext.theme changes.
 */
import Highcharts from './highcharts';

export const CHART_VAR_NAMES = {
    bg: '--chart-bg',
    text: '--chart-text',
    textSubtle: '--chart-text-subtle',
    grid: '--chart-grid',
    border: '--chart-border',
    surface: '--chart-surface',
    surfaceRaised: '--chart-surface-raised',
};

export function readChartVars(root = document.documentElement) {
    const cs = getComputedStyle(root);
    const out = {};
    for (const [key, name] of Object.entries(CHART_VAR_NAMES)) out[key] = cs.getPropertyValue(name).trim();
    return out;
}

export function buildThemeOptions(v) {
    const label = { style: { color: v.textSubtle, fontSize: '10px' } };
    return {
        chart: { backgroundColor: v.bg || 'transparent', style: { fontFamily: 'inherit', fontFeatureSettings: '"tnum"' } },
        title: { style: { color: v.text } },
        xAxis: { lineColor: v.border, tickColor: v.border, labels: label, crosshair: { color: v.border } },
        yAxis: { gridLineColor: v.grid, labels: label, title: { style: { color: v.textSubtle } } },
        legend: { itemStyle: { color: v.text, fontWeight: '500' }, itemHoverStyle: { color: v.text } },
        tooltip: { backgroundColor: v.surface, borderColor: v.border, style: { color: v.text } },
        navigator: {
            maskFill: 'rgba(125, 145, 175, 0.12)',
            maskInside: true,
            outlineColor: v.border,
            outlineWidth: 1,
            handles: { backgroundColor: v.surfaceRaised, borderColor: v.border },
            series: { type: 'areaspline', color: v.textSubtle, fillOpacity: 0.18, lineWidth: 1 },
            xAxis: { gridLineWidth: 0, labels: { style: { color: v.textSubtle, fontSize: '10px', textOutline: 'none' } } },
        },
        rangeSelector: {
            buttonTheme: {
                fill: v.surfaceRaised,
                stroke: v.border,
                'stroke-width': 1,
                r: 4,
                style: { color: v.textSubtle, fontWeight: '500' },
                states: {
                    hover: { fill: v.border },
                    select: { fill: 'var(--chart-wind)', style: { color: '#fff', fontWeight: '600' } },
                },
            },
            labelStyle: { color: v.textSubtle, fontWeight: '500' },
        },
        scrollbar: { enabled: false },
    };
}

export function applyChartTheme() {
    const opts = buildThemeOptions(readChartVars());
    Highcharts.setOptions(opts);
    // `update({})` would RESET live charts to defaults; pass the theme explicitly.
    // oneToOne must stay false: the theme's single-object xAxis/yAxis would
    // otherwise REMOVE every extra axis (barb lane, cloud-base axis).
    Highcharts.charts.forEach((c) => c && c.update(opts, true, false));
}
